/**
 * 往期列表顶上那一行图例该说什么。
 *
 * <p>Draws History 和 Pick 4 / Pick 4 Hourly 的 History 页签共用这一份判断 ——
 * 两边的竖条语义本来就是同一套（金=中了、绿=参与了、灰=没参与），说明文字分头
 * 写会各自漂移，而「同一个颜色在两页意思不一样」是这套设计最不能出的错。
 *
 * <p><b>为什么要有图例。</b> 竖条和爆奖金底 2026-09-11 / 09-13 就上线了，可它们
 * 的唯一解释在行的 `aria-label` 里 —— 只有读屏器读得到。2026-09-17 PM 拿着自己
 * 的 History 截图问「能不能区分参与和中奖」，问的就是已经做好的东西：做了但看不
 * 出来等于没做。
 *
 * <p><b>图例只解释屏幕上正在显示的东西。</b> 竖条整列收起时不许画竖条图例 ——
 * 那是在给一个不存在的东西写说明。同理，一行都没有的空列表不画图例。
 */

/** 图例里可能出现的项。`jackpot` 只属于 Pick 4 两档，票券抽奖每期必有一等奖。 */
export type LegendMark = 'won' | 'played' | 'none' | 'jackpot';

export type HistoryLegendView = {
  /** 按这个顺序渲染；空数组表示整行图例不画 */
  items: LegendMark[];
  /**
   * 非 null 时在图例位置显示「最近 %n% 期你没有参与」，n 就是这个数。
   *
   * <p>这一行回答的是「为什么这页没有我的标记」。它和 2026-09-11 评审否决的
   * 「整列灰条」不是一回事：整列灰条是每一行各对玩家说一遍「你没玩过」，这里
   * 是整页说一句。空白则是第三种，也是最差的一种 —— 玩家会以为功能不存在，
   * 这正是 09-17 那次提问的由来。
   */
  noRecent: number | null;
};

export function historyLegendView(input: {
  /** 登录了没有。访客不可能有参与记录，不对他说「你没有参与」 */
  signedIn: boolean;
  /** 竖条列这一次实际画了没有（调用方已有的 railOn，不要在这里重算） */
  railOn: boolean;
  /** 这一页有没有爆奖这种行（Pick 4 两档为 true，票券抽奖为 false） */
  jackpot: boolean;
  /**
   * 这一页是不是「只列我参与过的」。是的话不画「未参与」那一档 —— 这个列表里
   * 根本不存在那种行，给它写说明等于告诉玩家去找一个找不到的颜色。
   */
  mineOnly?: boolean;
  /** 这一页这次渲染了多少行 */
  rowCount: number;
  /** 这一次请求的期数上限（抽奖的 limit / Pick 4 的 window），用于那句话里的 %n% */
  window: number;
}): HistoryLegendView {
  // 空列表：没有任何行可解释。这时连爆奖图例也不画 —— 玩家看到的是一句
  // 「还没有往期」，下面跟一条颜色说明会很怪。
  if (input.rowCount <= 0) return { items: [], noRecent: null };

  const items: LegendMark[] = [];
  if (input.railOn) items.push('won', 'played');
  if (input.railOn && !input.mineOnly) items.push('none');
  if (input.jackpot) items.push('jackpot');

  // 登录了、这一页却一条参与记录都没有 —— 竖条列按既有规则收起了，用一句话
  // 顶上它的位置。窗口数从调用方传进来，不写死 50：Pick 4 的窗口是可切的
  // （100/500/1000），抽奖那边的 limit 将来也可能变。
  const noRecent = input.signedIn && !input.railOn && Number.isFinite(input.window) && input.window > 0
    ? Math.trunc(input.window)
    : null;

  return { items, noRecent };
}
